import React, { FC, useState, useEffect, useContext } from "react";
import { PhotoManagerContext } from "./index";
const uploadIcon = require("../../../../public/images/icon-upload.svg");
const noPosterIcon = require("../styles/images/noPoster.svg");
const editIcon = require("../styles/images/edit.svg");
const submitIcon = require("../../ManagePrepaidCustomers/components/images/submit.svg");
const cancelIcon = require("../../ManagePrepaidCustomers/components/images/cancel.svg");
import { Album, EventType } from "./types";
const showGalleryIcon = require("../styles/images/ShowGallery.svg");
const groupPhotosIcon = require("../styles/images/groupPhotos.svg");
import Telemetry from "../../../utils/rudderstackAnalytics";
import NewAlbumModal from "./NewAlbumModal";
import Breadcrumbs from "./Breadcrumbs";
import { useEditAlbumName } from "./useEditAlbumName";
const deleteIcon = require("../../../../app/assets/images/delete.svg");
import ConfirmDeleteModal from "./ConfirmDeleteModal";
import {
  decodeEntityPath,
  findEntityPath,
  getEncodedEntityPath,
  getEntitySegment
} from "./FolderFeatures/EncryptionOfFolder";

interface AlbumListProps {
  selectedEvent: EventType;
  albums: Album[];
  handleAlbumClick: (
    albumId: Album,
    encodedPath: string,
    event: EventType
  ) => void;
  navigateToRoot: () => void;
  photosLoading: number | null;

  showActionTooltip: (id: number, title: string, type: string) => void;
  onCreateAlbumSuccess: (newAlbum: Album) => void;
  updateAlbumsList: (album: Album | Album[]) => void;
  onEditSuccessUpdateAlbum?: (album: Album) => void;
}

const AlbumList: FC<AlbumListProps> = ({
  selectedEvent,
  albums,
  handleAlbumClick,
  navigateToRoot,
  photosLoading,

  showActionTooltip,
  onCreateAlbumSuccess,
  updateAlbumsList,
  onEditSuccessUpdateAlbum
}) => {
  const context = useContext(PhotoManagerContext);

  const [albumsData, setAlbumsData] = useState(albums);

  const [showModal, setShowModal] = useState(false);
  const [showDeleteAlbumModal, setShowDeleteAlbumModal] = useState(false);
  const [selectedAlbum, setSelectedAlbum] = useState<Album | null>(null);
  const {
    albumsInEditMode,
    albumNameDrafts,
    toggleEditMode,
    updateDraftName,
    handleKeyDownAction,
    submitNameChange
  } = useEditAlbumName(
    updateAlbumsList,
    setAlbumsData,
    albumsData,
    onEditSuccessUpdateAlbum
  );

  const handleAlbumClickWithNavigation = (album: Album, event: EventType) => {
    const url = new URL(window.location.href);
    const encodedPath = url.searchParams.get("p");

    const decodedPath = encodedPath ? decodeEntityPath(encodedPath) : "";

    const albumSegment = getEntitySegment(album);
    const fullPath = decodedPath
      ? `${decodedPath}/${albumSegment}`
      : albumSegment;

    const newEncodedPath = getEncodedEntityPath(fullPath);

    if (newEncodedPath) {
      context?.setEncodedString(newEncodedPath);

      handleAlbumClick(album, newEncodedPath, event);
    } else {
      console.error("Failed to encode album path");
    }
  };

  const handleCreateAlbumClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
  };

  useEffect(() => {
    setAlbumsData(albums);
  }, [albums]);

  useEffect(() => {
    const contentViewElement = document.getElementById("content-view");
    if (contentViewElement) {
      contentViewElement.scrollIntoView();
      window.scrollBy(0, -150);
    }
  }, []);

  if (!selectedEvent) {
    return (
      <div className="album-list-placeholder">
        Select an event to view albums.
      </div>
    );
  }

  return (
    <div className="ws-album-list-container">
      <Breadcrumbs viewType="AlbumList" />
      <div className="ws-album-cards-grid">
        {albumsData.map(album => (
          <div
            key={album.id}
            className="ws-album-card ws--folder"
            onClick={() => {
              handleAlbumClickWithNavigation(album, selectedEvent);
              Telemetry.track("content-library:album-opened", {
                source: "main-panel"
              });
            }}
          >
            <div className="ws-album-thumbnail">
              {album.thumbnailURL ? (
                <img
                  className="ws-album-thumbnail-img"
                  src={album.thumbnailURL}
                  alt={`Album ${album.name}`}
                />
              ) : (
                <div className="ws-no-poster">
                  <img src={noPosterIcon} className="ws-no-poster-icon" />
                  <span>No photos</span>
                </div>
              )}
            </div>
            {photosLoading === album.id && (
              <div className="ws-image-overlay-loader">
                <div className="ws-loading-spinner">
                  <span className="fa fa-spinner fa-spin ws-color-white"></span>
                </div>
              </div>
            )}
            <div className="ws-album-info">
              <div className="ws-album-info-text">
                {albumsInEditMode.length > 0 &&
                albumsInEditMode.includes(album.id) ? (
                  <div
                    className="ws-edit-album-name"
                    onClick={e => e.stopPropagation()}
                  >
                    <textarea
                      defaultValue={album.name}
                      onChange={e =>
                        updateDraftName(e, album.id, e.target.value)
                      }
                      onKeyDown={(
                        e: React.KeyboardEvent<HTMLTextAreaElement>
                      ) => handleKeyDownAction(e, album)}
                      onClick={e => e.stopPropagation()}
                      autoFocus
                      onFocus={e => {
                        const val = e.target.value;
                        e.target.value = "";
                        e.target.value = val;
                      }}
                    />

                    <div className="ws-edit-album-actions">
                      <img
                        className="actionIcon"
                        src={submitIcon}
                        onClick={e => submitNameChange(e, album)}
                      />
                      <img
                        className="actionIcon"
                        style={{ marginLeft: 5 }}
                        src={cancelIcon}
                        onClick={e =>
                          toggleEditMode(e, album.id, false, album.name)
                        }
                      />
                    </div>
                  </div>
                ) : (
                  <span className="ws-folder-name-div">
                    <span className="ws-edit-name-h3">
                      {albumNameDrafts
                        ? albumNameDrafts[album.id] || album.name
                        : album.name}
                    </span>
                    {album.groupPhotos && (
                      <span
                        data-toggle="tooltip"
                        data-placement="top"
                        data-container="body"
                        title="Group album"
                        id={`group-icon-${album.id}`}
                        onMouseEnter={e =>
                          showActionTooltip(album.id, "Group album", "group")
                        }
                        className="ws-album-info-icon ws-group-name-icon"
                      >
                        <img src={groupPhotosIcon} />
                      </span>
                    )}
                    {album.canRename && (
                      <span
                        className="ws-edit-name-icon"
                        data-toggle="tooltip"
                        data-placement="top"
                        data-container="body"
                        title="Edit name"
                        id={`edit-icon-${album.id}`}
                        onMouseEnter={e =>
                          showActionTooltip(album.id, "Edit Name", "edit")
                        }
                        onClick={e =>
                          toggleEditMode(e, album.id, true, album.name)
                        }
                      >
                        <img src={editIcon} alt="Edit album name" />
                      </span>
                    )}
                  </span>
                )}
                <p
                  className="ws-folder-ph-name"
                  style={{
                    marginLeft:
                      albumsInEditMode.length > 0 &&
                      albumsInEditMode.includes(album.id)
                        ? 8
                        : 0
                  }}
                >
                  {album.photographerName}
                </p>
              </div>

              <div className="ws-album-actions ws-album-list-actions album-action-icons">
                <div className="album-actions-left">
                  {album.canUpload && (
                    <a
                      id={`upload-icon-${album.id}`}
                      href={album.uploadPhotosPath}
                      className="ws-action-button upload"
                      onClick={e => e.stopPropagation()}
                      data-toggle="tooltip"
                      data-placement="top"
                      onMouseEnter={e =>
                        showActionTooltip(album.id, "Upload Photos", "upload")
                      }
                    >
                      <img src={uploadIcon} alt="Upload Photos" />
                    </a>
                  )}
                  <a
                    id={`view-icon-${album.id}`}
                    href={album.albumPath}
                    target="_blank"
                    className="ws-action-button upload"
                    onClick={e => e.stopPropagation()}
                    data-toggle="tooltip"
                    data-placement="top"
                    onMouseEnter={e =>
                      showActionTooltip(album.id, "View Album Gallery", "view")
                    }
                  >
                    <img src={showGalleryIcon} alt="View Album Gallery" />
                  </a>
                </div>
                {album.canCurrentUserDelete && (
                  <div className="album-actions-right">
                    <button
                      type="button"
                      id={`album-delete-icon-${album.id}`}
                      className="ws-action-button delete-btn"
                      onClick={e => {
                        e.stopPropagation();
                        setSelectedAlbum(album);
                        setShowDeleteAlbumModal(true);
                      }}
                      data-toggle="tooltip"
                      data-placement="bottom"
                      onMouseEnter={e =>
                        context?.showActionTooltip(
                          album.id,
                          "Delete Album",
                          "album-delete"
                        )
                      }
                    >
                      <img src={deleteIcon} alt="album-delete-icon" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
        {selectedEvent?.canCreateAlbums && (
          <div
            className="ws-album-card ws-create-album-card"
            onClick={e => handleCreateAlbumClick(e)}
          >
            <div className="folder-create-icon-div">
              <div className="folder-create-icon">+</div>
            </div>
            Create Album
          </div>
        )}
      </div>
      {showModal && (
        <NewAlbumModal
          eventId={selectedEvent.id}
          onClose={handleModalClose}
          onCreateAlbumSuccess={onCreateAlbumSuccess}
        />
      )}
      {showDeleteAlbumModal && context && selectedAlbum && (
        <ConfirmDeleteModal
          albumId={selectedAlbum.id}
          onClose={() => setShowDeleteAlbumModal(false)}
          onDeleteSuccess={() => {
            context.onAlbumDeleteSuccess(selectedAlbum.id, selectedAlbum);
            setShowDeleteAlbumModal(false);
          }}
          albumName={selectedAlbum.name}
        />
      )}
    </div>
  );
};

export default AlbumList;
